import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import './Header.css';

interface HeaderProps {}

const Header: React.FC<HeaderProps> = () => {
  const location = useLocation();
  const { t } = useTranslation();
  
  const [isTransparent, setIsTransparent] = useState(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return scrollTop < 50;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // 所有页面都应用透明导航栏效果，保持统一的用户体验
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      // 当滚动距离小于50px时，导航栏保持透明
      setIsTransparent(scrollTop < 50);
    };

    // 初始化时设置透明状态
    handleScroll();
    
    // 添加滚动监听
    window.addEventListener('scroll', handleScroll);
    
    // 清理函数
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  // 处理移动端菜单切换
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // 关闭移动端菜单
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // 当路由改变时关闭移动端菜单
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navigationItems = [
    { key: 'home', label: t('nav.home'), path: '/' },
    { key: 'about', label: t('nav.about'), path: '/about' },
    { key: 'services', label: t('nav.services'), path: '/services' },
    { key: 'solutions', label: t('nav.solutions'), path: '/solutions' },
    { key: 'news', label: t('nav.news'), path: '/news' },
    { key: 'contact', label: t('nav.contact'), path: '/contact' }
  ];

  return (
    <header className={`header ${isTransparent ? 'header-transparent' : ''}`}>
      <div className="header-container">
        {/* Logo区域 */}
        <div className="logo-section">
          <Link to="/" className="logo">
            <img src="/images/logo.webp" alt="柯熙美诺 Kosemet" className="logo-image" />
          </Link>
        </div>

        {/* 桌面端导航菜单 */}
        <nav className="navigation">
          <ul className="nav-list">
            {navigationItems.map((item) => (
              <li key={item.key} className="nav-item">
                <Link 
                  to={item.path} 
                  className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
          {/* 语言切换器 - 桌面端 */}
          <LanguageSwitcher />
        </nav>

        {/* 移动端汉堡菜单按钮 */}
        <button 
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="切换菜单"
          aria-expanded={isMobileMenuOpen}
        >
          <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
        </button>

        {/* 移动端导航菜单 */}
        <nav className={`mobile-navigation ${isMobileMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-list">
            {navigationItems.map((item) => (
              <li key={item.key} className="mobile-nav-item">
                <Link 
                  to={item.path} 
                  className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
          {/* 语言切换器 - 移动端 */}
          <LanguageSwitcher />
        </nav>

        {/* 移动端菜单遮罩层 */}
        {isMobileMenuOpen && (
          <div 
            className="mobile-menu-overlay"
            onClick={closeMobileMenu}
          ></div>
        )}

      </div>
    </header>
  );
};

export default Header;
